import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@shared/ui';
import { useEffect, useRef, useState } from 'react';
import AppointmentCard from '../appointments/AppointmentCard';
import AdminAppointmentActionsDialog from './AdminAppointmentActionsDialog';
import { COACHING_ADOPT_MESSAGE } from './adminAppointmentErrors';
import {
  useGetAdminAppointmentHistoryQuery,
  useGetAdminAppointmentQuery,
} from './api/adminAppointmentsApi';
import { useAdminAppointmentOperation } from './useAdminAppointmentOperation';

interface AdminAppointmentDetailPageProps {
  appointmentId: string;
}

export function AdminAppointmentDetailPage({ appointmentId }: AdminAppointmentDetailPageProps) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const { data: appointment, isLoading, refetch } = useGetAdminAppointmentQuery(appointmentId);
  const { data: history, refetch: refetchHistory } =
    useGetAdminAppointmentHistoryQuery(appointmentId);
  const operationId = latestOperationId(history);
  const { data: operation } = useAdminAppointmentOperation(operationId, [appointmentId]);
  const refreshedOperation = useRef<string | null>(null);

  useEffect(() => {
    if (!operation || operation.status === 'pending' || refreshedOperation.current === operation.id)
      return;
    refreshedOperation.current = operation.id;
    refetch();
    refetchHistory();
  }, [operation, refetch, refetchHistory]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <CircularProgress size={20} />
        </CardContent>
      </Card>
    );
  }

  if (!appointment) {
    return (
      <Alert severity="error">
        <Typography>Appointment details could not be loaded.</Typography>
        <Typography variant="body2">No scheduling changes were made.</Typography>
        <Button variant="outlined" onClick={() => refetch()} sx={{ mt: 1 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <AppointmentCard appointment={appointment} />
      {appointment.kind === 'coaching' && !appointment.series_id && (
        <Alert severity="warning">{COACHING_ADOPT_MESSAGE}</Alert>
      )}
      {(appointment.action_capabilities.reschedule.allowed ||
        appointment.action_capabilities.cancel.allowed) && (
        <Box>
          <Button variant="contained" onClick={() => setActionsOpen(true)}>
            Manage appointment
          </Button>
        </Box>
      )}

      {operation && (
        <Card>
          <CardContent>
            <Typography>{`Operation ${operation.command} is ${operation.status}`}</Typography>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <Typography component="h2" variant="h6">
            History
          </Typography>
          {history?.records.map((record) => (
            <Typography key={record.id}>{record.type}</Typography>
          ))}
        </CardContent>
      </Card>
      {actionsOpen && (
        <AdminAppointmentActionsDialog
          open
          appointmentId={appointment.id}
          viewerTimezone={appointment.current_schedule.timezone}
          onClose={() => setActionsOpen(false)}
          onComplete={() => {
            refetch?.();
            refetchHistory?.();
          }}
        />
      )}
    </Box>
  );
}

function latestOperationId(history: ReturnType<typeof useGetAdminAppointmentHistoryQuery>['data']) {
  for (const record of [...(history?.records ?? [])].reverse()) {
    const operationId = record.payload.operation_id;
    if (typeof operationId === 'string' && operationId.length > 0) return operationId;
  }
  return null;
}

export default AdminAppointmentDetailPage;
